<template>
  <div class="w-64">
    <div
      class="flex items-center justify-between mb-2 pb-2 border-b border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)]"
    >
      <h3 class="font-medium text-sm text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]">
        {{
          showMenu
            ? t('whatsapp.file_upload.send_files')
            : t('whatsapp.file_upload.selected_count', { count: selectedFiles.length })
        }}
      </h3>
      <Button text rounded severity="secondary" @click="emit('close')">
        <template #icon>
          <X :size="18" />
        </template>
      </Button>
    </div>

    <div v-if="showMenu" class="space-y-0.5">
      <Button
        v-for="option in fileTypeOptions"
        :key="option.id"
        :label="option.label"
        text
        class="w-full !justify-start h-auto py-2 px-2 text-xs!"
        @click="handleFileTypeSelect(option)"
      >
        <template #icon>
          <component :is="option.icon" :size="16" :stroke-width="2" class="mr-2" />
        </template>
      </Button>
    </div>

    <div v-else>
      <input
        ref="fileInputRef"
        type="file"
        multiple
        class="hidden"
        :accept="currentAccept"
        :capture="currentCapture"
        @change="handleFileChange"
      />

      <div v-if="selectedFiles.length === 0" class="text-center py-8">
        <p class="text-xs text-[var(--p-surface-500)] mb-4">
          {{ t('whatsapp.file_upload.no_files_selected') }}
        </p>
        <AppButton
          :label="t('whatsapp.file_upload.back_to_menu')"
          outlined
          size="small"
          class="w-full"
          @click="handleBackToMenu"
        />
      </div>

      <div v-else>
        <div class="max-h-48 overflow-y-auto mb-4 space-y-2">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="flex items-center gap-3 p-2 rounded-lg bg-[var(--p-surface-100)] dark:bg-[var(--p-surface-800)] hover:bg-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-700)] transition-colors"
          >
            <component
              :is="getFileIcon(file)"
              :size="20"
              :stroke-width="2"
              class="text-[var(--p-surface-500)] flex-shrink-0"
            />
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium truncate text-[var(--p-surface-900)] dark:text-[var(--p-surface-0)]"
              >
                {{ file.name }}
              </p>
              <p class="text-xs text-[var(--p-surface-500)]">{{ formatFileSize(file.size) }}</p>
            </div>
            <Button text rounded severity="danger" @click="removeFile(index)">
              <template #icon>
                <X :size="18" />
              </template>
            </Button>
          </div>
        </div>

        <div class="flex gap-2">
          <Button
            :label="t('whatsapp.file_upload.back')"
            outlined
            size="small"
            class="flex-1"
            @click="handleBackToMenu"
          />
          <Button
            :label="t('whatsapp.file_upload.send')"
            severity="primary"
            size="small"
            class="flex-1"
            @click="handleSend"
          />
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="showCameraDialog"
      modal
      :header="t('whatsapp.file_upload.take_photo')"
      :style="{ width: '92vw', maxWidth: '560px' }"
      appendTo="self"
      :draggable="false"
      @hide="handleCloseCamera"
      @show="startCamera"
    >
      <div class="space-y-4">
        <div
          class="relative aspect-[4/3] overflow-hidden rounded-lg bg-[var(--p-surface-950)] flex items-center justify-center"
        >
          <video
            ref="cameraVideoRef"
            autoplay
            playsinline
            muted
            class="h-full w-full object-cover"
            @loadedmetadata="handleCameraReady"
          />

          <div
            v-if="isCameraLoading"
            class="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white"
          >
            {{ t('whatsapp.file_upload.opening_camera') }}
          </div>

          <div
            v-if="cameraError"
            class="absolute inset-0 flex items-center justify-center bg-black/80 p-6 text-center text-sm text-white"
          >
            {{ cameraError }}
          </div>
        </div>

        <canvas ref="cameraCanvasRef" class="hidden" />

        <PrimeMessage v-if="cameraError" severity="warn" :closable="false">
          {{ t('whatsapp.file_upload.camera_permission_hint') }}
        </PrimeMessage>
      </div>

      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <AppButton
            :label="t('whatsapp.file_upload.cancel')"
            severity="secondary"
            outlined
            @click="handleCloseCamera"
          />
          <!-- <AppButton
            :label="t('whatsapp.file_upload.select_file')"
            outlined
            severity="secondary"
            @click="openCameraFilePicker"
          /> -->
          <AppButton
            :label="t('whatsapp.file_upload.capture')"
            severity="primary"
            :disabled="
              Boolean(cameraError) || isCameraLoading || !isCameraReady || isCapturingPhoto
            "
            :loading="isCapturingPhoto"
            @click="handleCapturePhoto"
          >
            <template #icon-start>
              <Camera :size="18" />
            </template>
          </AppButton>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import PrimeMessage from 'primevue/message'
import { FileText, Image, Camera, Music, FolderOpen, X } from 'lucide-vue-next'
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFileFormatter } from '../../composables/useFileFormatter'
import type { FilePreviewCategory } from '../../composables/useFiles'
import AppButton from '@/components/atoms/buttons/AppButton.vue'

const emit = defineEmits<{
  'files-select': [files: File[], category?: FilePreviewCategory]
  close: []
}>()

const { formatFileSize } = useFileFormatter()
const { t } = useI18n()

const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFiles = ref<File[]>([])
const showMenu = ref(true)
const currentAccept = ref('*')
const currentCapture = ref<'environment' | undefined>(undefined)
const currentCategory = ref<FilePreviewCategory>('all')
const showCameraDialog = ref(false)
const cameraVideoRef = ref<HTMLVideoElement | null>(null)
const cameraCanvasRef = ref<HTMLCanvasElement | null>(null)
const cameraStream = ref<MediaStream | null>(null)
const cameraError = ref('')
const isCameraLoading = ref(false)
const isCameraReady = ref(false)
const isCapturingPhoto = ref(false)
const isFilePickerOpen = ref(false)

const fileTypeOptions = computed(() => [
  {
    id: 'document',
    label: t('whatsapp.file_upload.document'),
    icon: FileText,
    accept: '.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z',
    category: 'document' as const,
  },
  {
    id: 'photos-videos',
    label: t('whatsapp.file_upload.photos_and_videos'),
    icon: Image,
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png,video/*,.mov',
    category: 'media' as const,
  },
  {
    id: 'camera',
    label: t('whatsapp.file_upload.camera'),
    icon: Camera,
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
    capture: 'environment' as const,
    category: 'image' as const,
  },
  {
    id: 'audio',
    label: t('whatsapp.file_upload.audio'),
    icon: Music,
    // Meta-compatible formats only: AAC, AMR, MP3, MP4-audio (m4a), OGG/OPUS
    accept: '.aac,.amr,.mp3,.m4a,.ogg,audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg',
    category: 'audio' as const,
  },
  {
    id: 'all-files',
    label: t('whatsapp.file_upload.all_files'),
    icon: FolderOpen,
    accept: '*',
    category: 'all' as const,
  },
])

const handleFileTypeSelect = (option: (typeof fileTypeOptions.value)[number]) => {
  currentAccept.value = option.accept
  currentCapture.value = option.capture
  currentCategory.value = option.category
  showMenu.value = false

  if (option.id === 'camera') {
    openCamera()
    return
  }

  openFilePicker()
}

const handleFileChange = (e: Event) => {
  isFilePickerOpen.value = false
  const target = e.target as HTMLInputElement
  if (target.files) {
    const MAX_SIZE = 16 * 1024 * 1024 // 16 MB — Meta limit
    const filesArray = Array.from(target.files)
    const oversized = filesArray.filter((f) => f.size > MAX_SIZE)
    if (oversized.length > 0) {
      alert(
        t('whatsapp.file_upload.files_too_large', {
          files: oversized.map((f) => f.name).join('\n'),
        }),
      )
    }
    const valid = filesArray.filter((f) => f.size <= MAX_SIZE)
    if (valid.length > 0) {
      emit('files-select', valid, currentCategory.value)
      selectedFiles.value = []
      showMenu.value = true
      emit('close')
    } else {
      selectedFiles.value = []
      showMenu.value = true
      emit('close')
    }

    target.value = ''
  }
}

const openFilePicker = () => {
  isFilePickerOpen.value = true

  window.addEventListener(
    'focus',
    () => {
      setTimeout(() => {
        if (!isFilePickerOpen.value) return
        isFilePickerOpen.value = false
        selectedFiles.value = []
        showMenu.value = true
        emit('close')
      }, 250)
    },
    { once: true },
  )

  setTimeout(() => {
    fileInputRef.value?.click()
  }, 0)
}

const removeFile = (index: number) => {
  selectedFiles.value = selectedFiles.value.filter((_, i) => i !== index)
}

const handleSend = () => {
  if (selectedFiles.value.length > 0) {
    emit('files-select', selectedFiles.value, currentCategory.value)
    selectedFiles.value = []
    emit('close')
  }
}

const handleBackToMenu = () => {
  selectedFiles.value = []
  showMenu.value = true
}

const stopCamera = () => {
  cameraStream.value?.getTracks().forEach((track) => track.stop())
  cameraStream.value = null
  isCameraReady.value = false

  if (cameraVideoRef.value) {
    cameraVideoRef.value.srcObject = null
  }
}

const startCamera = async () => {
  cameraError.value = ''
  isCameraLoading.value = true
  isCameraReady.value = false
  stopCamera()

  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(t('whatsapp.file_upload.camera_not_supported'))
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false,
    })

    cameraStream.value = stream

    await nextTick()

    const video = cameraVideoRef.value
    if (!video) {
      throw new Error(t('whatsapp.file_upload.camera_view_error'))
    }

    video.srcObject = stream
    await video.play()
  } catch {
    cameraError.value =
      t('whatsapp.file_upload.camera_access_error')
  } finally {
    isCameraLoading.value = false
  }
}

const openCamera = () => {
  showCameraDialog.value = true
}

const handleCameraReady = () => {
  const video = cameraVideoRef.value
  isCameraReady.value = Boolean(video && video.videoWidth > 0 && video.videoHeight > 0)
}

const handleCloseCamera = () => {
  stopCamera()
  showCameraDialog.value = false
  cameraError.value = ''
  isCameraLoading.value = false
  isCameraReady.value = false
  isCapturingPhoto.value = false

  if (selectedFiles.value.length === 0) {
    showMenu.value = true
  }
}

const openCameraFilePicker = () => {
  handleCloseCamera()
  currentAccept.value = '.jpg,.jpeg,.png,image/jpeg,image/png'
  currentCapture.value = 'environment'
  showMenu.value = false

  openFilePicker()
}

const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.92)
  })
}

const handleCapturePhoto = async () => {
  const video = cameraVideoRef.value
  const canvas = cameraCanvasRef.value

  if (!video || !canvas || video.videoWidth === 0 || video.videoHeight === 0) {
    cameraError.value = t('whatsapp.file_upload.camera_not_ready')
    return
  }

  isCapturingPhoto.value = true

  try {
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    if (!context) {
      cameraError.value = t('whatsapp.file_upload.photo_processing_error')
      return
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const blob = await canvasToBlob(canvas)
    if (!blob) {
      cameraError.value = t('whatsapp.file_upload.image_creation_error')
      return
    }

    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })
    stopCamera()
    showCameraDialog.value = false
    showMenu.value = true

    await nextTick()

    emit('files-select', [file], 'image')
  } finally {
    isCapturingPhoto.value = false
  }
}

onUnmounted(() => {
  isFilePickerOpen.value = false
  stopCamera()
})

const getFileIcon = (file: File): Component => {
  if (file.type.startsWith('image/')) return Image
  if (file.type.startsWith('video/')) return Image
  if (file.type.startsWith('audio/')) return Music
  if (file.type.includes('pdf') || file.type.includes('document')) return FileText
  return FolderOpen
}
</script>
